/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from "@wordpress/i18n";

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useState } from "@wordpress/element";
import { registerBlockType } from '@wordpress/blocks';
import {
  useBlockProps,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  RichText,
  BlockControls,
  ColorPalette,
  InnerBlocks
} from "@wordpress/block-editor";

import {
  PanelBody,
  PanelRow,
  TextControl,
  Button,
  RadioControl,
  RangeControl,
  ToolbarButton,
  Toolbar,
  ToolbarGroup,
} from "@wordpress/components";

import { ChromePicker } from "react-color";
import { brush } from '@wordpress/icons';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @param {Object}   props               Properties passed to the function.
 * @param {Object}   props.attributes    Available block attributes.
 * @param {Function} props.setAttributes Function that updates individual attributes.
 *
 * @return {Element} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
  const { cards, textColor } = attributes;

  const [isColorPaletteVisible, setColorPaletteVisible] = useState(false);

  const toggleColorPalette = () => {
    setColorPaletteVisible(!isColorPaletteVisible);
  };

  const blockProps = useBlockProps();

  const addCard = () => {
    const newCard = {
      title: "Card title " + (cards.length + 1),
      logo: "",
      text: "Initial text",
    };
    setAttributes({ cards: [...cards, newCard] });
  };

  const updateCard = (index, newCard) => {
    const updatedCards = [...cards];
    updatedCards[index] = newCard;
    setAttributes({ cards: updatedCards });
  };

  const removeCard = (index) => {
    const updatedCards = cards.filter((_, i) => i !== index);
    setAttributes({ cards: updatedCards });
  };

  return (
    <>
      <BlockControls>
        <ToolbarGroup>
          {/* Icon button to toggle ColorPalette visibility */}
          <ToolbarButton
          icon={brush}
            label={__("Text Color", "small-cards")}
            onClick={toggleColorPalette}
          />
        </ToolbarGroup>
        {isColorPaletteVisible && (
          <div style={{ padding: "8px", background: "white" }}>
            <ColorPalette
              value={textColor}
              onChange={(color) => {
                setAttributes({ textColor: color });
                setColorPaletteVisible(false); // Close the palette after selection
              }}
            />
          </div>
        )}
      </BlockControls>

      <InspectorControls group="settings">
        {cards.map((card, index) => (
          <PanelBody
            title={__("Card " + (index + 1) + " Settings", "small-cards")}
            key={`panelBodyCards-${index}`}
          >
            <PanelRow>
              <label>
                {__("Card " + (index + 1) + " Logo", "small-cards")}
              </label>
            </PanelRow>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) =>
                  updateCard(index, { ...card, logo: media.url })
                }
                allowedTypes={["image"]}
                value={card.logo}
                render={({ open }) => (
                  <>
                    <Button
                      onClick={open}
                      style={{ background: "#007CBA", color: "white" }}
                    >
                      {card.logo ? "Change Logo" : "Upload Logo"}
                    </Button>
                    {card.logo && (
                      <img
                        src={card.logo}
                        alt={__("Card " + (index + 1) + " logo", "small-cards")}
                        style={{ objectFit: "contain", height: "50px" }}
                      />
                    )}
                  </>
                )}
              />
            </MediaUploadCheck>
            <TextControl
              label={__("Card " + (index + 1) + " Title", "small-cards")}
              value={card.title}
              onChange={(value) => updateCard(index, { ...card, title: value })}
            />
            <TextControl
              label={__("Card " + (index + 1) + " Text", "small-cards")}
              value={card.text}
              onChange={(value) => updateCard(index, { ...card, text: value })}
            />
            <Button
              onClick={() => removeCard(index)}
              isDestructive
              style={{ marginTop: "10px" }}
            >
              {__("Remove Card", "small-cards")}
            </Button>
          </PanelBody>
        ))}

        <Button onClick={addCard} isPrimary style={{ marginTop: "20px" }}>
          {__("Add New Card", "small-cards")}
        </Button>
        {/* Repeat similar blocks for other cards */}
      </InspectorControls>
      <InspectorControls group="styles">
        <PanelBody title={__("All Cards Settings", "small-cards")}>
          <PanelRow>
            <RangeControl
              label={__("Cards Width (% of the row)", "small-cards")}
              value={attributes.flexBasis}
              onChange={(value) => setAttributes({ flexBasis: value })}
              min={10}
              max={99}
            />
          </PanelRow>
          <PanelRow>
            <RangeControl
              label={__("Gap between card", "small-cards")}
              value={attributes.gap}
              onChange={(value) => setAttributes({ gap: value })}
              min={1}
              max={500}
            />
          </PanelRow>
          <PanelRow>
            <RadioControl
              label={__(
                "Wrap the row (in case the cards pass the width limit they will go to the next line)",
                "small-cards"
              )}
              selected={attributes.flexWrap}
              options={[
                { label: __("One line only", "small-cards"), value: "nowrap" },
                { label: __("Wrap row", "small-cards"), value: "wrap" },
              ]}
              onChange={(e) => setAttributes({ flexWrap: e })}
            />
          </PanelRow>
          <PanelRow>
            <RadioControl
              label={__(
                "Hide text if you want to only show the logo",
                "small-cards"
              )}
              selected={attributes.hideText}
              options={[
                { label: __("Show everything", "small-cards"), value: " " },
                { label: __("Hide text", "small-cards"), value: "none" },
              ]}
              onChange={(e) => setAttributes({ hideText: e })}
            />
          </PanelRow>
        </PanelBody>
        <PanelBody title={__("Colors", "small-cards")}>
          <PanelRow>
            <label>{__("BG Color", "small-cards")}</label>
          </PanelRow>
          <PanelRow>
            <ChromePicker
              color={attributes.backgroundColor}
              onChangeComplete={(color) => {
                setAttributes({
                  backgroundColor: `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`,
                });
              }}
            />
          </PanelRow>
          <PanelRow>
            <label>{__("Backdrop Filter", "small-cards")}</label>
            <TextControl
              value={attributes.backdropFilter}
              onChange={(value) => setAttributes({ backdropFilter: value })}
            />
          </PanelRow>
        </PanelBody>
        <PanelBody title={__("Cards Radius", "small-cards")}>
          <PanelRow>
            <label>{__("◰ Top-left", "small-cards")}</label>
            <TextControl
              value={attributes.borderRadiusTopLeft}
              onChange={(value) =>
                setAttributes({ borderRadiusTopLeft: value })
              }
            />
          </PanelRow>
          <PanelRow>
            <label>{__("◳ Top-right", "small-cards")}</label>
            <TextControl
              value={attributes.borderRadiusTopRight}
              onChange={(value) =>
                setAttributes({ borderRadiusTopRight: value })
              }
            />
          </PanelRow>
          <PanelRow>
            <label>{__("◲ Bottom-right", "small-cards")}</label>
            <TextControl
              value={attributes.borderRadiusBottomRight}
              onChange={(value) =>
                setAttributes({ borderRadiusBottomRight: value })
              }
            />
          </PanelRow>
          <PanelRow>
            <label>{__("◱ Bottom-left", "small-cards")}</label>
            <TextControl
              value={attributes.borderRadiusBottomLeft}
              onChange={(value) =>
                setAttributes({ borderRadiusBottomLeft: value })
              }
            />
          </PanelRow>
        </PanelBody>
        <PanelBody title={__("Responsive", "small-cards")}>
          <PanelRow>
            <RadioControl
              label={__("Mobile Template", "small-cards")}
              selected={attributes.mobileTemplateClass}
              options={[
                {
                  label: __("One Card per row", "small-cards"),
                  value: "mobile_one-card",
                },
                {
                  label: __("Two Cards per row", "small-cards"),
                  value: "mobile_two-cards",
                },
              ]}
              onChange={(e) => setAttributes({ mobileTemplateClass: e })}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <section {...blockProps}>
        <div
          className={attributes.mobileTemplateClass}
          style={{
            display: "flex",
            gap: attributes.gap + "px",
            flexWrap: attributes.flexWrap,
          }}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              style={{
                flexBasis: `${attributes.flexBasis}%`,
                textAlign: "center",
                padding: attributes.padding,
                background: attributes.backgroundColor,
                backdropFilter: attributes.backdropFilter,
                borderRadius: `${attributes.borderRadiusTopLeft} ${attributes.borderRadiusTopRight} ${attributes.borderRadiusBottomRight} ${attributes.borderRadiusBottomLeft}`,
                color: textColor,
              }}
            >
              {card.logo && (
                <img
                  src={card.logo}
                  alt={__("Card " + (index + 1) + " logo", "small-cards")}
                  style={{
                    width: "100%",
                    height: "50px",
                    objectFit: "contain",
                    ...card.styles,
                  }}
                />
              )}
              <RichText
                tagName="h3"
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  display: attributes.hideText,
                  color: textColor,
                }}
                className=""
                placeholder={__("Card " + (index + 1) + " name", "small-cards")}
                value={card.title}
                onChange={(val) => updateCard(index, { ...card, title: val })}
              />
              <RichText
                tagName="p"
                style={{
                  fontSize: "14px",
                  fontWeight: "400",
                  display: attributes.hideText,
                  color: textColor,
                }}
                className=""
                placeholder={__("Card " + (index + 1) + " text", "small-cards")}
                value={card.text}
                onChange={(val) => updateCard(index, { ...card, text: val })}
              />
            </div>
          ))}
        </div>
        <InnerBlocks />
      </section>
    </>
  );
}
